import React from 'react';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';
import { FormattedMessage, injectIntl } from 'react-intl';
import isEmail from 'validator/lib/isEmail';
import Button from '../../components/Button';
import Menu from '../../components/Menu';
import Form from '../../components/Form';
import Label from '../../components/Label';
import Message from '../../components/Message';
import Icon from '../../components/Icon';
import Grid from '../../components/Grid';
import Toast from '../../components/Toast';
import Dimmer from '../../components/Dimmer';
import Loader from '../../components/Loader';
import CalendarFeedTextBoxWithCopyButton from '../../components/CalendarFeedTextBoxWithCopyButton';
import revokeCalendarFeedSecurityTokenMutation from './revokeCalendarFeedSecurityTokenMutation';
import { SimpleLoadingComponent } from '../../utils/apollo';

import './styles.css';
import messages from './messages';

export const GOOGLE_CALENDAR = 1;
export const ICALENDAR_OTHERS = 2;

const GoogleCalendarLoadingMessage = () => (
  <div>
    <Message icon styleName="loading-msg">
      <Icon name="spinner" loading />
      <Message.Content>
        <div styleName="bold"><FormattedMessage {...messages.shareGoogleLoadingTitle} /></div>
        <br />
        <FormattedMessage {...messages.shareGoogleLoadingDescription} />
      </Message.Content>
    </Message>
    <FormattedMessage {...messages.shareGoogleLoadingHint} />
  </div>
);

class ShareCalendarComponent extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    viewer: PropTypes.object,
    revokeCalendarFeedSecurityToken: PropTypes.func.isRequired,
    selectedTab: PropTypes.number,
  };

  state = {
    selectedTab: this.props.selectedTab || GOOGLE_CALENDAR,
    email: null,
    invalidEmail: false,
    requestingGoogle: false,
    requesting: false,
    iCalURLCreated: false,
    iCalURLNotCreated: false,
  };

  onChange = (e, { value }) => {
    value = value || e.target.value;
    this.setState({ email: value });
  };

  setSelectedTab = (newTab) => {
    const _state = { selectedTab: newTab };

    // resetting success messages
    if (this.state.iCalURLCreated) {
      _state.iCalURLCreated = false;
    }

    this.setState(_state);
  };

  shareGoogleCalendar = (e) => {
    const { email } = this.state;
    e.preventDefault();
    if (email && isEmail(email)) {
      this.setState({ invalidEmail: false, requestingGoogle: true });
    } else {
      this.setState({ invalidEmail: true });
    }
  };

  resetICalURL = () => {
    const { revokeCalendarFeedSecurityToken } = this.props;
    this.setState({ requesting: true });
    revokeCalendarFeedSecurityToken({
      variables: {
        input: {},
      },
    }).then(() => {
      this.setState({
        iCalURLNotCreated: false,
        requesting: false,
        iCalURLCreated: true,
      });
    }).catch(() => {
      this.setState({
        iCalURLNotCreated: true,
        requesting: false,
        iCalURLCreated: false,
      });
    });
  };

  render() {
    const { intl, viewer } = this.props;
    const {
      email,
      invalidEmail,
      requestingGoogle,
      requesting,
      iCalURLNotCreated,
      selectedTab,
      iCalURLCreated,
    } = this.state;

    const GOOGLE_CALENDAR_SYNC_FEATURE_FLAG = 'REPVISITS_GOOGLE_CALENDAR_SYNC';
    const googleCalendarSyncEnabled = viewer.enabledFeatures.includes(GOOGLE_CALENDAR_SYNC_FEATURE_FLAG);

    const isGoogleCalendarSelected = googleCalendarSyncEnabled && (selectedTab === GOOGLE_CALENDAR);
    const isICalendarOthersSelected = (selectedTab === ICALENDAR_OTHERS) || (googleCalendarSyncEnabled === false);

    return (
      <Dimmer.Dimmable>
        <Dimmer
          role="progressbar"
          aria-describedby={intl.formatMessage(messages.loading)}
          aria-busy={requesting}
          inverted
          active={requesting}
        >
          <Loader active={requesting} />
        </Dimmer>
        <FormattedMessage {...messages.shareYourCalendarDescription} />
        <Menu pointing secondary>
          {googleCalendarSyncEnabled && (
          <Menu.Item
            onClick={() => { this.setSelectedTab(GOOGLE_CALENDAR); }}
            active={isGoogleCalendarSelected}
            styleName={`menu-item ${isGoogleCalendarSelected ? 'active' : ''}`}
          >
            <FormattedMessage {...messages.googleCalendar} />
          </Menu.Item>
          ) }
          <Menu.Item
            onClick={() => { this.setSelectedTab(ICALENDAR_OTHERS); }}
            active={isICalendarOthersSelected}
            styleName={`menu-item ${isICalendarOthersSelected ? 'active' : ''}`}
          >
            <FormattedMessage {...messages.outlookOthers} />
          </Menu.Item>
        </Menu>
        {isGoogleCalendarSelected &&
        (requestingGoogle ? <GoogleCalendarLoadingMessage /> : (
          <Form
            id="share-google-calendar-form"
            onSubmit={this.shareGoogleCalendar}
          >
            <Grid>
              <Grid.Column width={13}>
                <Form.Field error={invalidEmail}>
                  <Form.Input
                    type="text"
                    value={email || ''}
                    name="email"
                    onChange={this.onChange}
                    placeholder={intl.formatMessage(messages.mailPlaceholder)}
                  >
                    <input
                      id="share-google-email"
                      aria-invalid={invalidEmail}
                      aria-required="true"
                      aria-label={intl.formatMessage(messages.email)}
                    />
                  </Form.Input>
                  {invalidEmail && (
                  <Label role="tooltip" basic color="red" pointing>
                    <FormattedMessage {...messages.enterValidEmail} />
                  </Label>
                  )}
                </Form.Field>
              </Grid.Column>
              <Grid.Column width={3}>
                <Button type="submit" primary floated="right">
                  <FormattedMessage {...messages.share} />
                </Button>
              </Grid.Column>
            </Grid>
            <br />
            <FormattedMessage {...messages.shareGoogleDescription} />
          </Form>
        ))}
        {isICalendarOthersSelected && (
        <div>
          <div><FormattedMessage {...messages.importICalendar} /></div>
          <br />
          <CalendarFeedTextBoxWithCopyButton token={viewer.calendarFeedSecurityToken} copy={copy} />
          <br />
          <div>
            <FormattedMessage
              {...messages.importICalendarDescription}
              values={{
                b: (...chunks) => <strong>{chunks}</strong>,
                linebreak: <br />,
              }}
            />
          </div><br />
          {/* eslint-disable-next-line max-len */}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
          <a
            tabIndex={0}
            onClick={this.resetICalURL}
            data-cy="repVisits-share-calendar-reset-link"
            styleName="reset-link"
          >
            <FormattedMessage {...messages.resetURL} />
          </a>
          {iCalURLNotCreated && (
          <Message error>
            <Message.Header><FormattedMessage {...messages.iCalURLNotCreated} /></Message.Header>
            <p><FormattedMessage {...messages.tryAgain} /></p>
          </Message>
          )}
          <Toast
            active={iCalURLCreated}
            onDeactivate={() => this.setState({ iCalURLCreated: false })}
            description={intl.formatMessage(messages.iCalURLCreated)}
          />
        </div>
        )}
      </Dimmer.Dimmable>
    );
  }
}

const query = gql`
  query ShareCalendar {
    viewer {
      id
      calendarFeedSecurityToken
      enabledFeatures
    }
  }
`;
const graphqlData = graphql(query);
const mutateRevokeCalendarFeedSecurityToken = graphql(revokeCalendarFeedSecurityTokenMutation, {
  name: 'revokeCalendarFeedSecurityToken',
});

export const ShareCalendarPageComponent = injectIntl(ShareCalendarComponent);
export default SimpleLoadingComponent(graphqlData, mutateRevokeCalendarFeedSecurityToken)(ShareCalendarPageComponent);
